/**
 * PlanBriefHeader — the "research brief" surface shown during plan approval.
 *
 * Parses the Förståelse av frågan / Förväntat resultat / Källor jag planerar
 * att konsultera sections from /plan.md and renders them in a compact
 * three-block brief. This turns the approval moment from a bureaucratic
 * checkbox into a contract: the user sees exactly what they're approving.
 */
import { useMemo } from "react"

import { useTranslation } from "react-i18next"

import { FileText, Lightbulb, Package, X as XIcon } from "@/components/icons"
import { cn } from "@/lib/utils"

import { FORMAT_INFO, parseDeliverableType } from "./deliverable-format-utils"

interface BriefSections {
  understanding?: string
  expectedResult?: string
  sources?: string[]
}

/**
 * Extract a named section's body from a plan markdown string. Matches the
 * section header loosely (case-insensitive, ignoring leading `##`).
 */
function extractSection(content: string, headerPatterns: RegExp[]): string {
  const lines = content.split("\n")
  let inSection = false
  const body: string[] = []
  for (const line of lines) {
    const header = /^#{1,3}\s+(.+?)\s*$/.exec(line)
    if (header) {
      const text = header[1].trim()
      if (inSection) break
      if (headerPatterns.some((re) => re.test(text))) {
        inSection = true
        continue
      }
      continue
    }
    if (inSection) body.push(line)
  }
  return body.join("\n").trim()
}

function parseBriefSections(content: string): BriefSections {
  const understanding = extractSection(content, [
    /^Förståelse av frågan$/i,
    /^Understanding( of the question)?$/i,
    /^Förståelse$/i,
  ])
  const expectedResult = extractSection(content, [
    /^Förväntat resultat$/i,
    /^Expected result$/i,
    /^You['’]ll get$/i,
  ])
  const rawSources = extractSection(content, [
    /^Källor jag planerar att konsultera$/i,
    /^Källor$/i,
    /^Sources( I['’]ll consult)?$/i,
  ])
  const sources = rawSources
    .split("\n")
    .map((l) => l.replace(/^[-*\d.]+\s*/, "").trim())
    .filter((l) => l.length > 0 && !l.startsWith("<!--"))
  return {
    understanding: understanding || undefined,
    expectedResult: expectedResult || undefined,
    sources: sources.length > 0 ? sources : undefined,
  }
}

interface PlanBriefHeaderProps {
  planContent: string
  /** Deliverable type override from the store (wins over parsed content). */
  deliverableType?: string | null
  /**
   * UI-4: when provided, each rendered source chip gains a close button
   * that calls back with the source text. Without this prop, chips are
   * read-only — the brief is also displayed outside the approval flow
   * (e.g. on a finished run) where editing makes no sense.
   */
  onRemoveSource?: (source: string) => void
}

export function PlanBriefHeader({
  planContent,
  deliverableType,
  onRemoveSource,
}: PlanBriefHeaderProps) {
  const { t } = useTranslation()
  const brief = useMemo(() => parseBriefSections(planContent), [planContent])
  const resolvedType =
    (deliverableType as keyof typeof FORMAT_INFO | null) ?? parseDeliverableType(planContent)
  const formatInfo = resolvedType ? FORMAT_INFO[resolvedType] : null

  // When nothing parsed, render nothing (fall back to the plain plan view).
  if (!brief.understanding && !brief.expectedResult && !brief.sources && !formatInfo) {
    return null
  }

  return (
    <section
      aria-label={t("deepExplorer.plan_brief_aria")}
      className="border-border bg-muted/30 shrink-0 border-b px-3 py-2"
    >
      {formatInfo && (
        <div className="mb-2 flex items-center gap-1.5">
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium",
              formatInfo.accentClass
            )}
          >
            {formatInfo.icon}
            <span>{t(formatInfo.labelKey)}</span>
          </span>
          <span className="text-muted-foreground text-[11px]">
            - {t("deepExplorer.planned_format")}
          </span>
        </div>
      )}

      <div className="grid grid-cols-1 gap-2">
        {brief.understanding && (
          <div className="flex gap-2">
            <Lightbulb className="text-primary mt-0.5 size-3.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-muted-foreground text-[10px] font-semibold tracking-wide uppercase">
                {t("deepExplorer.plan_understanding")}
              </p>
              <p className="text-foreground text-xs leading-snug">{brief.understanding}</p>
            </div>
          </div>
        )}
        {brief.expectedResult && (
          <div className="flex gap-2">
            <FileText className="text-primary mt-0.5 size-3.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-muted-foreground text-[10px] font-semibold tracking-wide uppercase">
                {t("deepExplorer.plan_expected_result")}
              </p>
              <p className="text-foreground text-xs leading-snug">{brief.expectedResult}</p>
            </div>
          </div>
        )}
        {brief.sources && brief.sources.length > 0 && (
          <div className="flex gap-2">
            <Package className="text-primary mt-0.5 size-3.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-muted-foreground text-[10px] font-semibold tracking-wide uppercase">
                {t("deepExplorer.plan_sources")}
              </p>
              <ul className="mt-0.5 flex flex-wrap gap-1">
                {brief.sources.slice(0, 6).map((src, i) => {
                  const display = src.length > 48 ? `${src.slice(0, 48)}…` : src
                  return (
                    <li
                      key={i}
                      className={cn(
                        "bg-background border-border text-foreground flex items-center gap-1 rounded border text-[11px]",
                        onRemoveSource ? "py-0.5 pr-0.5 pl-1.5" : "px-1.5 py-0.5"
                      )}
                    >
                      <span>{display}</span>
                      {onRemoveSource && (
                        <button
                          className="text-muted-foreground hover:text-destructive -m-1 flex size-6 items-center justify-center rounded"
                          type="button"
                          aria-label={t("deepExplorer.remove_source", {
                            source: display,
                            defaultValue: `Remove source ${display}`,
                          })}
                          onClick={() => onRemoveSource(src)}
                        >
                          <XIcon className="size-3" />
                        </button>
                      )}
                    </li>
                  )
                })}
                {brief.sources.length > 6 && (
                  <li className="text-muted-foreground px-1 py-0.5 text-[11px]">
                    +{brief.sources.length - 6}
                  </li>
                )}
              </ul>
            </div>
          </div>
        )}
      </div>
    </section>
  )
}

// Exported for unit testing / reuse.
